import { Box, ButtonBase, makeStyles, Typography } from "@material-ui/core";
import React from "react";
import { useHistory } from "react-router-dom";

import { CustomButton } from "@/components/Button";
import { Chevron } from "@/components/Chevron";
import { homeCardStyle } from "@/components/home/HomeSection";
import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import { BaseTemplate } from "@/layouts/BaseTemplate";
import theme from "@/theme";

export const useExclusiveLayout = makeStyles({
  body: {
    padding: `${layout.space[16]}px ${layout.space[24]}px ${layout.space[32]}px`,
    color: colors["text/primary"],
    minWidth: 0,
    [theme.breakpoints.up("md")]: { padding: `${layout.space[32]}px 0 ${layout.space[48]}px` },
  },
  pcHeader: {
    display: "none",
    [theme.breakpoints.up("md")]: {
      display: "flex",
      flexDirection: "column",
      gap: layout.space[12],
      marginBottom: layout.space[24],
    },
  },
  back: {
    ...textStyles["PC/Button/M"],
    color: colors["text/link"],
    minHeight: 40,
    padding: layout.space[12],
    margin: -layout.space[12],
    alignSelf: "flex-start",
    gap: layout.space[4],
  },
  title: { ...textStyles["PC/Heading/H2"], overflowWrap: "anywhere" },
  subtitle: { ...textStyles["PC/Body/Small"], color: colors["text/secondary"] },
  sectionTitle: { ...textStyles["SP/Heading/H2"], [theme.breakpoints.up("md")]: textStyles["PC/Heading/H3"] },
  stack: {
    display: "flex",
    flexDirection: "column",
    gap: layout.space[24],
    minWidth: 0,
    "& > $pcHeader": { marginBottom: 0 },
  },
  card: {
    backgroundColor: colors["surface/card"],
    borderRadius: layout.radius.lg,
    padding: layout.space[24],
    minWidth: 0,
  },
  empty: {
    maxWidth: 560,
    width: "100%",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: layout.space[24],
  },
  emptyTitle: { ...textStyles["SP/Heading/H3"], [theme.breakpoints.up("md")]: textStyles["PC/Heading/H4"] },
  small: {
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    overflowWrap: "anywhere",
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Small"],
  },
  text: {
    ...textStyles["SP/Body/Large"],
    color: colors["text/secondary"],
    overflowWrap: "anywhere",
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Large"],
  },
  learn: {
    ...homeCardStyle,
    padding: layout.space[16],
    [theme.breakpoints.up("md")]: { padding: layout.space[24] },
    [theme.breakpoints.between("md", "md")]: { padding: layout.space[16] },
    display: "flex",
    flexDirection: "column",
    gap: layout.space[8],
  },
  learnTitle: { ...textStyles["SP/Heading/H4"], [theme.breakpoints.up("md")]: textStyles["PC/Heading/H4"] },
  learnButton: {
    [theme.breakpoints.up("md")]: {
      "&&&": { alignSelf: "flex-start", width: "100%", maxWidth: 240, minWidth: 0, marginLeft: 0 },
    },
  },
  error: { display: "flex", flexDirection: "column", gap: layout.space[16], alignItems: "flex-start" },
});

export const ExclusivePageHeading: React.FC<{
  title: string;
  backLabel: string;
  onBack: () => void;
  subtitle?: string;
  badge?: React.ReactNode;
}> = ({ title, backLabel, onBack, subtitle, badge }) => {
  const classes = useExclusiveLayout();
  return (
    <Box className={classes.pcHeader}>
      <ButtonBase className={classes.back} onClick={onBack}>
        <Chevron direction="back" />
        {backLabel}
      </ButtonBase>
      <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={layout.space[12]}>
        <Typography component="h1" className={classes.title}>
          {title}
        </Typography>
        {badge}
      </Box>
      {subtitle && <Typography className={classes.subtitle}>{subtitle}</Typography>}
    </Box>
  );
};

export const LearningContentLink: React.FC<{ empty?: boolean }> = ({ empty = false }) => {
  const classes = useExclusiveLayout();
  const history = useHistory();
  return (
    <Box className={classes.learn}>
      <Typography component="h2" className={classes.learnTitle}>
        {empty ? "いまは記事で学べます" : "不動産投資を学びたいときは"}
      </Typography>
      <Typography className={classes.small}>
        {empty
          ? "基礎から出口戦略まで、全23章の記事にまとめています。"
          : "基礎から出口戦略まで、全23章にまとめています。"}
      </Typography>
      <CustomButton
        buttonSize="M"
        className={classes.learnButton}
        customVariant="outlinedPrimary"
        onClick={() => history.push("/learning")}
      >
        学習コンテンツはこちら
      </CustomButton>
    </Box>
  );
};

export const ExclusiveFetchError: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => {
  const classes = useExclusiveLayout();
  return (
    <Box role="alert" className={`${classes.card} ${classes.error}`}>
      <Typography className={classes.text}>{message}</Typography>
      <CustomButton buttonSize="M" customVariant="outlinedPrimary" onClick={onRetry}>
        再試行
      </CustomButton>
    </Box>
  );
};

// PCの見出しはページ内に置き、共有テンプレートの既存表示は変更しない。
export const ExclusiveTemplate: React.FC<React.ComponentProps<typeof BaseTemplate>> = (props) => {
  const { isMobile } = useDevice();
  return (
    <BaseTemplate
      {...props}
      headerTitle={isMobile ? props.headerTitle : undefined}
      onBack={isMobile ? props.onBack : undefined}
    />
  );
};
